// Entity names (PLAN §4 `entity_names`): every name is stored as written, plus a script-specific folded form
// (Arabic alef/hamza/diacritics, Latin accents), an automatic Latin transliteration and a phonetic key. These
// feed search and entity-resolution suggestions (task 17); they never confirm an identity on their own (§6.1).

export const NAME_SCRIPTS = ['Latn', 'Arab', 'Hebr', 'Cyrl', 'Grek', 'Hani', 'Other', 'Mixed'] as const;
export type NameScript = (typeof NAME_SCRIPTS)[number];

const SCRIPT_TESTS: [Exclude<NameScript, 'Other' | 'Mixed'>, RegExp][] = [
  ['Latn', /\p{Script=Latin}/u],
  ['Arab', /\p{Script=Arabic}/u],
  ['Hebr', /\p{Script=Hebrew}/u],
  ['Cyrl', /\p{Script=Cyrillic}/u],
  ['Grek', /\p{Script=Greek}/u],
  ['Hani', /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u],
];

/** ISO 15924 code of the script most letters are written in; `Mixed` when two scripts each hold ≥ 25%. */
export function detectScript(value: string): NameScript {
  const counts = new Map<NameScript, number>();
  let letters = 0;
  for (const ch of value) {
    if (!/\p{L}/u.test(ch)) continue;
    letters++;
    const s = SCRIPT_TESTS.find(([, re]) => re.test(ch))?.[0] ?? 'Other';
    counts.set(s, (counts.get(s) ?? 0) + 1);
  }
  if (!letters) return 'Other';
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  const [first, second] = ranked;
  if (second && second[1] / letters >= 0.25) return 'Mixed';
  return first?.[0] ?? 'Other';
}

const ch = (code: number) => String.fromCharCode(code);
/** Arabic harakat, Quranic marks, superscript alef (built from code points to keep the source ASCII). */
const ARABIC_DIACRITICS = new RegExp(`[${ch(0x610)}-${ch(0x61a)}${ch(0x64b)}-${ch(0x65f)}${ch(0x670)}${ch(0x6d6)}-${ch(0x6ed)}]`, 'g');
const TATWEEL = new RegExp(ch(0x640), 'g');

/** Arabic folding: no diacritics or tatweel; alef forms → ا; hamza carriers → و / ي; ى → ي; ة → ه. */
export const foldArabic = (s: string): string =>
  s
    .replace(ARABIC_DIACRITICS, '')
    .replace(TATWEEL, '')
    .replace(/[أإآٱ]/g, 'ا')
    .replace(/ؤ/g, 'و')
    .replace(/[ئى]/g, 'ي')
    .replace(/ة/g, 'ه')
    .replace(/ء/g, '');

/**
 * Script-specific folded form used for matching: lower case, accents removed (Latin, Greek), Arabic folded,
 * punctuation → single spaces. "Franc-Tireur" → "franc tireur"; "إبراهيم" → "ابراهيم".
 */
export function normalizeName(value: string): string {
  const s = foldArabic(value.normalize('NFKC'))
    .replace(/ß/g, 'ss')
    .replace(/[æÆ]/g, 'ae')
    .replace(/[œŒ]/g, 'oe')
    .replace(/[øØ]/g, 'o')
    .replace(/[łŁ]/g, 'l')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return s || value.trim().toLowerCase();
}

const ARABIC_LATIN: Record<string, string> = {
  ا: 'a',
  ب: 'b',
  ت: 't',
  ث: 'th',
  ج: 'j',
  ح: 'h',
  خ: 'kh',
  د: 'd',
  ذ: 'dh',
  ر: 'r',
  ز: 'z',
  س: 's',
  ش: 'sh',
  ص: 's',
  ض: 'd',
  ط: 't',
  ظ: 'z',
  ع: '',
  غ: 'gh',
  ف: 'f',
  ق: 'q',
  ك: 'k',
  ل: 'l',
  م: 'm',
  ن: 'n',
  ه: 'h',
  ة: 'a',
  پ: 'p',
  چ: 'ch',
  ژ: 'zh',
  گ: 'g',
  ک: 'k',
  ی: 'i',
};

function arabicToLatin(s: string): string {
  const src = s.replace(ARABIC_DIACRITICS, '').replace(TATWEEL, '').replace(/[أإٱ]/g, 'ا').replace(/آ/g, 'اا');
  let out = '';
  const chars = [...src];
  chars.forEach((ch, i) => {
    const prev = chars[i - 1];
    const next = chars[i + 1];
    const wordStart = !prev || !/\p{Script=Arabic}/u.test(prev);
    if (ch === 'و' || ch === 'ؤ') out += wordStart || prev === 'ا' || next === 'ا' ? 'w' : 'u';
    else if (ch === 'ي' || ch === 'ى' || ch === 'ئ') out += wordStart || next === 'ا' || next === 'و' ? 'y' : 'i';
    else if (ch === 'ع') out += wordStart ? 'a' : '';
    else if (ch === 'ء') out += '';
    else if (ch === 'ة') out += 'a';
    else if (ch === 'ه' && !next) out += 'h';
    else out += ARABIC_LATIN[ch] ?? ch;
  });
  return out;
}

const CYRILLIC_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
  і: 'i',
  ї: 'yi',
  є: 'ye',
  ґ: 'g',
};

const GREEK_LATIN: Record<string, string> = {
  α: 'a',
  β: 'v',
  γ: 'g',
  δ: 'd',
  ε: 'e',
  ζ: 'z',
  η: 'i',
  θ: 'th',
  ι: 'i',
  κ: 'k',
  λ: 'l',
  μ: 'm',
  ν: 'n',
  ξ: 'x',
  ο: 'o',
  π: 'p',
  ρ: 'r',
  σ: 's',
  ς: 's',
  τ: 't',
  υ: 'y',
  φ: 'f',
  χ: 'ch',
  ψ: 'ps',
  ω: 'o',
};

const HEBREW_LATIN: Record<string, string> = {
  א: 'a',
  ב: 'b',
  ג: 'g',
  ד: 'd',
  ה: 'h',
  ו: 'v',
  ז: 'z',
  ח: 'ch',
  ט: 't',
  י: 'y',
  כ: 'k',
  ך: 'k',
  ל: 'l',
  מ: 'm',
  ם: 'm',
  נ: 'n',
  ן: 'n',
  ס: 's',
  ע: '',
  פ: 'p',
  ף: 'f',
  צ: 'ts',
  ץ: 'ts',
  ק: 'k',
  ר: 'r',
  ש: 'sh',
  ת: 't',
};

const HEBREW_POINTS = new RegExp(`[${ch(0x591)}-${ch(0x5c7)}]`, 'g');
const mapChars = (s: string, table: Record<string, string>) =>
  [...s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()].map(ch => table[ch] ?? ch).join('');

/**
 * Automatic Latin transliteration of a non-Latin name ("حسن" → "hsn", "Путин" → "putin"), folded to lower case.
 * Arabic short vowels aren't written, so the result is a matching aid, not a spelling. Null for Latin names.
 */
export function transliterate(value: string): string | null {
  const script = detectScript(value);
  if (script === 'Latn' || script === 'Other' || script === 'Hani') return null;
  let s = value.normalize('NFKC');
  if (/\p{Script=Arabic}/u.test(s)) s = arabicToLatin(s);
  if (/\p{Script=Cyrillic}/u.test(s)) s = mapChars(s, CYRILLIC_LATIN);
  if (/\p{Script=Greek}/u.test(s)) s = mapChars(s, GREEK_LATIN);
  if (/\p{Script=Hebrew}/u.test(s)) s = mapChars(s.replace(HEBREW_POINTS, ''), HEBREW_LATIN);
  const out = normalizeName(s);
  return /\p{L}/u.test(out) ? out : null;
}

function phoneticWord(w: string): string {
  const s = w
    .replace(/[^a-z]/g, '')
    .replace(/ph/g, 'f')
    .replace(/(sch|sh|ch)/g, 's')
    .replace(/(th|dh)/g, 't')
    .replace(/kh/g, 'k')
    .replace(/gh/g, 'g')
    .replace(/ck/g, 'k')
    .replace(/qu?/g, 'k')
    .replace(/x/g, 'ks')
    .replace(/c(?=[eiy])/g, 's')
    .replace(/c/g, 'k')
    .replace(/z/g, 's')
    .replace(/w/g, 'v')
    .replace(/y/g, 'i')
    .replace(/dj|j/g, 'j');
  if (!s) return '';
  const head = /[aeiou]/.test(s[0] ?? '') ? 'a' : (s[0] ?? '');
  const tail = s
    .slice(1)
    .replace(/[aeiouh]/g, '')
    .replace(/(.)\1+/g, '$1');
  return (head + tail).replace(/(.)\1+/g, '$1');
}

/**
 * Phonetic key over the Latin form (transliterated first): consonant skeleton per word with common spelling
 * variants merged (h and vowels after the first letter dropped): "Mohamed", "Mohammed", "Muhammad" and "محمد" → "md";
 * "Hasan", "Hassan", "حسن" → "hsn".
 */
export function phoneticKey(value: string): string {
  const latin = transliterate(value) ?? normalizeName(value);
  return latin.split(' ').map(phoneticWord).filter(Boolean).join(' ');
}

export interface NameForms {
  value: string;
  script: NameScript;
  normalized: string;
  latinTranslit: string | null;
  phoneticKey: string;
}

/** Every derived form stored with a name. */
export function nameForms(value: string): NameForms {
  const v = value.trim().replace(/\s+/g, ' ');
  return {
    value: v,
    script: detectScript(v),
    normalized: normalizeName(v),
    latinTranslit: transliterate(v),
    phoneticKey: phoneticKey(v),
  };
}
